// MARK: NPM Modules
import React, { useState } from "react";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import ReduxStyleguide from "types/redux/styleguide";
import Layout from "types/pages/blog/post/layout";
import SEOMetaData from "types/seo/seoMetaData";
// MARK: Components
import PageBoundingBoxContainer from "components/common/layouts/containers/pageBoundingContainer";
import MultilineHeadline from "components/common/typography/multilineHeadline";
import BlogContentBlocks from "./components/blogContentBlocks";
import BlogPostAuthor from "./components/blogPostAuthor";
import Form from "components/forms/contact/form";
import IdentityDivider from "components/common/dividers/identity/withBars";
import AlertBar from "components/common/alertBar";
import BlogPostHero from "./components/blogPostHero";
import BookAConsultation from "components/forms/bookConsultation";

// MARK: React Component
// Props
interface Props {
  layout: Layout;
  seo: SEOMetaData;
}
// Component
function BlogPostLayout({ layout, seo }: Props) {
  // Mark: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: State Variables
  const [alertBarOpacity, setAlertBarOpacity] = useState<number>(0);
  const [alertMessage, setAlertMessage] = useState<string>("");
  // MARK: Variables
  const { author } = seo;

  const {
    containerWidth,
    maximumBlogWidth,
    navigationBarHeight,
    margin,
  } = styleguide;
  const layoutWidth =
    containerWidth > maximumBlogWidth ? maximumBlogWidth : containerWidth;

  const { multilineHeadline, hero } = layout;

  // MARK: Functionality
  const copiedURLLink = () => {
    setAlertMessage("Copied to Clipboard.");
    displayAlert();
  };

  const contactFormIncomplete = (message: string) => {
    setAlertMessage(message);
    setAlertBarOpacity(1);
    setTimeout(() => {
      setAlertBarOpacity(0);
    }, 3000);
  };

  const displayAlert = () => {
    setAlertBarOpacity(1);
    setTimeout(() => {
      setAlertBarOpacity(0);
    }, 3000);
  };

  // MARK: Render
  return (
    <PageBoundingBoxContainer
      maximumBodyWidth={layoutWidth}
      margin={margin}
      paddingTop={navigationBarHeight}
    >
      <AlertBar opacity={alertBarOpacity}>{alertMessage}</AlertBar>
      <PageBoundingBoxContainer
        as={"article"}
        paddingTop={0}
        margin={0}
        maximumBodyWidth={layoutWidth}
      >
        <PageBoundingBoxContainer
          as={"section"}
          aria-label={layout.title + "hero"}
          paddingTop={0}
          margin={0}
          maximumBodyWidth={layoutWidth}
        >
          <PageBoundingBoxContainer
            aria-label={layout.title}
            paddingTop={0}
            margin={0}
            maximumBodyWidth={layoutWidth}
          >
            <MultilineHeadline headlines={multilineHeadline} />
            <BlogPostAuthor 
              author={author} 
              dateCreated={seo.dateCreated} 
              dateUpdated={seo.dateUpdated} 
            />
          </PageBoundingBoxContainer>
          <BlogPostHero 
            hero={hero}
            blogHeroSubtitle={layout.blogHeroSubtitle}
          />
        </PageBoundingBoxContainer>
        <PageBoundingBoxContainer
          as={"section"}
          aria-label={layout.title + "content"}
          paddingTop={0}
          margin={0}
          maximumBodyWidth={layoutWidth}
        >
          <BlogContentBlocks 
            blogContentBlocks={layout.blogContentBlocks}
            copiedURLLink={() => {
              copiedURLLink()
            }}
          />
        </PageBoundingBoxContainer>
      </PageBoundingBoxContainer>
      <IdentityDivider />
      <Form
        includeHeaders={true}
        onSubmittionError={(message) => contactFormIncomplete(message)}
      />
      <BookAConsultation
        onUpdateAlertBar={(message) => contactFormIncomplete(message)}
      />
    </PageBoundingBoxContainer>
  );
}

export default BlogPostLayout;
